import React, {useEffect} from "react";
import styles from "./TranscriptTable.module.css";
import {TranscriptTableHeader} from "./header/TranscriptTableHeader";
import {useAppDispatch, useAppSelector} from "../../../../app/hooks";
import {TablePagination} from "../../../elements/pagination/TablePagination";
import {RequestStatus} from "../../../../api/apiConfiguration";
import {
    fetchTranscriptList,
    TRANSCRIPT_TABLE_ITEMS_ON_PAGE,
    transcriptListRequestStatusSelector,
    transcriptListSelector,
    transcriptListTotalCountSelector,
    transcriptTableRequestSelector,
    updateTranscriptTableRequest,
} from "../transcriptListSlice";
import {TranscriptTableRow} from "./row/TranscriptTableRow";
import {TableLoader} from "../../../elements/tableLoader/TableLoader";
import {TranscriptTableControls} from "./controls/TranscriptTableControls";
import {showErrorToastNotification} from "../../../elements/toastNotification/toastNotificationSlice";

interface TranscriptTableProps {
}

export const TranscriptTable: React.FC<TranscriptTableProps> = () => {

    const dispatch = useAppDispatch()
    const transcriptList = useAppSelector(transcriptListSelector)
    const transcriptListTotalCount = useAppSelector(transcriptListTotalCountSelector)
    const transcriptTableRequest = useAppSelector(transcriptTableRequestSelector)
    const transcriptListRequestStatus = useAppSelector(transcriptListRequestStatusSelector)

    const {
        pageNumber,
        publicBotIdList,
        startDate,
        endDate,
        followUp,
        chatOutcome,
        searchQuery,
        conversationStatusList
    } = transcriptTableRequest

    useEffect(() => {
        dispatch(fetchTranscriptList())
    }, [dispatch, pageNumber, publicBotIdList, startDate, endDate, followUp, chatOutcome, searchQuery, conversationStatusList]);

    useEffect(() => {
        if (transcriptListRequestStatus === RequestStatus.REJECTED) {
            dispatch(showErrorToastNotification({message: "Failed to load transcripts"}))
        }
    }, [dispatch, transcriptListRequestStatus]);

    const isLoading = transcriptListRequestStatus === RequestStatus.PENDING;
    const isResolved = transcriptListRequestStatus === RequestStatus.RESOLVED;
    const showEmptyState = isResolved && transcriptListTotalCount === 0;

    return <div className={styles.wrapper}>
        <div className={styles.tableWrapper}>
            <TranscriptTableControls/>
            <TranscriptTableHeader/>
            <div className={styles.tableRowsWrapper}>
                {isLoading && <TableLoader/>}

                {isResolved &&
                    transcriptList.length > 0 &&
                    transcriptList.map((transcript, index) => (
                    <React.Fragment key={`transcript-table-row-${transcript.conversationId ?? index}`}>
                        <TranscriptTableRow
                            index={index}
                            transcript={transcript}
                            isLastRow={index === transcriptList.length - 1}
                        />
                    </React.Fragment>
                ))}
            </div>
        </div>

        {showEmptyState ? (
            <div className={styles.emptyTableWrapper}>
                <div className={styles.titleWrapper}>
                    <span className={styles.title}>No transcripts found</span>
                    <span className={styles.subtitle}>Try changing the filters or date range.</span>
                </div>
            </div>
        ) : isResolved && (
            <TablePagination
                pageNumber={transcriptTableRequest.pageNumber}
                totalItems={transcriptListTotalCount}
                itemsPerPage={TRANSCRIPT_TABLE_ITEMS_ON_PAGE}
                onPageChange={(newPage) => dispatch(updateTranscriptTableRequest({pageNumber: newPage}))}
            />
        )}
    </div>
}
